<template>
    <div
        ref="highlighter"
        class="highlight-element"
    />
    <div
        class="tour-container"
    >
        <div
            v-if="step > 0"
            class="tour-step"
            :style="`top: ${posY}px; left: ${posX + (steps[step - 1].inNav ? 0 : 65)}px;`"
        >
            <q-card class="q-pa-md">
                <q-card-section
                    v-if="steps[step - 1].header?.title"
                    class="text-white"
                >
                    <div class="text-h6">
                        {{ steps[step - 1].header.title }}
                    </div>
                </q-card-section>
                <div class="tour-text">
                    {{ steps[step - 1].content }}
                </div>
                <div class="tour-controls q-mt-sm">
                    <div>
                        Step {{ step }} of {{ steps.length }}
                    </div>
                    <q-btn
                        v-if="step > 1"
                        icon="mdi-chevron-left"
                        flat
                        :ripple="false"
                        data-cy="tour-back"
                        @click="step -= 1"
                    />
                    <q-btn
                        v-if="step < steps.length"
                        icon="mdi-chevron-right"
                        flat
                        :ripple="false"
                        data-cy="tour-next"
                        @click="step += 1"
                    />
                    <q-btn
                        v-if="step < steps.length"
                        color="white"
                        icon="mdi-close"
                        flat
                        data-cy="tour-leave"
                        @click="cancelTour"
                    />
                    <q-btn
                        v-else
                        color="white"
                        label="finish tour"
                        flat
                        data-cy="tour-leave"
                        @click="cancelTour"
                    />
                </div>
            </q-card>
        </div>
    </div>
    <q-dialog
        v-model="tourIntro"
        persistent
        backdrop-filter="blur(2px) brightness(1)"
    >
        <q-card
            class="intro-popup q-pa-md"
        >
            <div class="text-h5">
                Welcome to Flux Forecast
            </div>
            <p>
                Let's start with a short tour to help you find your way around.
            </p>
            <div class="col">
                <div class="row">
                    <q-btn
                        color="white"
                        label="Cancel"
                        flat
                        data-cy="tour-cancel"
                        @click="cancelTour"
                    />
                    <q-btn
                        class="q-ml-md"
                        color="white"
                        label="Begin Tour"
                        outline
                        data-cy="tour-start"
                        @click="startTour"
                    />
                </div>
            </div>
        </q-card>
    </q-dialog>
</template>

<script setup>
import { onMounted, ref, useTemplateRef, watch } from 'vue';

const steps = [
    {
        target: 'home',
        header: { title: 'Home' },
        inNav: true,
        content: "In the Home tab, view all locations within your account, including those that have not reached their risk threshold.",
        params: { placement: 'left' }
    },
    {
        target: 'expand-all',
        content: "Click expand all to view all locations within each group on the sidebar. ",
        params: { placement: 'left' },
    },
    {
        target: 'filter-assets',
        content: "Use the filter button to narrow your view, including only showing locations currently under alert.",
        params: { placement: 'left' }
    },
    {
        target: 'group-0',
        content: "Click on a group to see its locations. Select Overview or an individual location in the sidebar to open forecast information for the selected item, including basin conditions and forecast precipitation.",
        params: { placement: 'left' }
    },
    {
        target: 'group-0',
        content: "Flux Forecast averages several high resolution forecast models to produce a forecast ensemble for each location. Expand the forecasts for each location to view the rainfall models and snowmelt model that make up the forecast ensemble.",
        params: { placement: 'left' }
    },
    {
        target: 'layers-button',
        content: "Click the Layers drawer to turn on map layers, including hourly rain, watersheds, gauges, fires, and forecast models.",
        params: { placement: 'right' }
    },
    {
        target: 'reports',
        header: { title: 'Reports' },
        inNav: true,
        content: 'In the Reports tab, generate and download reports for any asset associated with your account. Reports are available for each location at every forecast period (twice daily).',
        params: { placement: 'left' }
    },
    {
        target: 'history',
        header: { title: 'History' },
        inNav: true,
        content: 'In History, view estimates of past conditions at your assets.',
        params: { placement: 'left' }
    },
    {
        target: 'settings',
        header: { title: 'Settings' },
        inNav: true,
        content: 'In the Settings tab, change settings for alert thresholds and notifications.',
        params: { placement: 'left' }
    },
    {
        target: 'gravitar-image-container',
        header: { title: 'Menu' },
        inNav: true,
        content: 'Click the menu to access more features, including user settings and methodology.',
        params: { placement: 'left' }
    },
];
const tourIntro = ref(false)
const step = ref(0);
const posY = ref(0);
const posX = ref(60);
const highlighter = useTemplateRef('highlighter');

const emit = defineEmits(['end-tour'])

watch(() => step.value, (currentStep) => {
    if (currentStep <= steps.length && currentStep > 0) {
        setHighlightPosition(steps[currentStep - 1]);
    } else {
        step.value = 0;
        emit('end-tour', false)
    }
});

onMounted(() => {
    tourIntro.value = true;
    window.addEventListener('keydown', e => keyEvent(e));
    window.addEventListener('resize', () => setHighlightPosition(steps[step.value - 1]))
});

const keyEvent = (e) => {
    if (e.key === 'ArrowRight') {
        changeTourStep(1);
    } else if (e.key === 'ArrowLeft') {
        changeTourStep(-1);
    } else if (e.key === 'Escape') {
        changeTourStep(-100);
        closeTour();
    }
};

const changeTourStep = (direction) => {
    if (tourIntro.value) {
        tourIntro.value = false;
        step.value = 1;
    } else {
        step.value += direction
        if (step.value >= steps.length || step.value < 0) {
            closeTour();
        } else if (highlighter.value) {
            highlighter.value.style.width = '0px';
        }
    }
};

const closeTour = () => {
    window.removeEventListener("mousedown", keyEvent, true);
};

const setHighlightPosition = (currentStep) => {
    const currentElement = document.getElementById(currentStep.target);
    if (!currentElement) return;

    let additionalOffsetY = 0;
    if(currentStep.params.placement === 'bottom') {
        additionalOffsetY = currentElement.offsetHeight + 20;
    } else if(currentStep.params.placement === 'top') {
        additionalOffsetY = -150 - 20; // Assuming the height of the tour card is 150px
    }

    const groupTable = document.getElementById('group-table');
    // special case for the group table row highlighting
    if(currentStep.target.includes('group-')) {
        // highlighted row should be at the top
        if(!groupTable) return;
        additionalOffsetY = groupTable.offsetTop;
    }

    posY.value = (currentElement.offsetTop + additionalOffsetY > window.innerHeight - 200) ? window.innerHeight - 200 : currentElement.offsetTop + additionalOffsetY;

    let additionalOffsetX = 0;
    if(currentStep.params.placement === 'left') {
        additionalOffsetX = currentElement.offsetWidth + 20;
    } else if(currentStep.params.placement === 'right') {
        additionalOffsetX = -400 - 20; // Assuming the width of the tour card is 400px
    }
    posX.value = currentElement.offsetLeft + additionalOffsetX;


    // special cases for the group table row highlighting due to the parent div being offset correctly
    // not the prettiest solution, but seems to work. Feel free to change this section if you want Max
    let additionalHighlightOffsetTop = 0;
    if(currentStep.target.includes('group-')) {
        // highlighted row should be at the top
        if(!groupTable) return;
        additionalHighlightOffsetTop = groupTable.offsetTop;
    }
    highlighter.value.style.left = `${currentElement.offsetLeft + (currentStep.inNav ? 0 : 65)}px`;
    highlighter.value.style.width = `${currentElement.offsetWidth}px`;
    highlighter.value.style.top = `${currentElement.offsetTop + additionalHighlightOffsetTop}px`;
    highlighter.value.style.height = `${currentElement.clientHeight}px`;
}

const startTour = () => {
    tourIntro.value = false;
    step.value += 1;
};

const cancelTour = () => {
    emit('end-tour');
};

</script>

<style lang="scss">
.highlight-element {
    position: fixed;
    box-shadow: 0 0 0 99999px rgba(0, 0, 0, .5);
    z-index: 999 !important;
    pointer-events: none;
}

.tour-container {
    position: absolute;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    z-index: 1000 !important;

    .q-card__section--vert {
        padding: 0.5rem 0;
    }

    .tour-step {
        display: flex;
        position: relative;
        width: 400px;
        z-index: 100;

        .notch {
            position: absolute;
            top: calc(50% - 10px);
            left: -10px;
            height: 20px;
            width: 20px;
            transform: rotate(45deg);
            z-index: 10;
        }

        div {
            color: white;
            background-color: #50596C;
        }

        .tour-controls {
            display: flex;
            align-items: center;
            width: 100%;
            justify-content: space-between;
        }
    }
}

.tour-highlight {
    position: absolute;
}

.intro-popup {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    position: absolute;
    background-color: #50596C !important;
    color: white !important;
}
</style>
